import { useState } from 'react';
import { FileText, ClipboardList, ScanLine, X, Loader2, ExternalLink, Package } from 'lucide-react';
import api from '../../lib/api';
import { FacturaViewer } from '../FacturaViewer';
import { OCViewer } from '../OCViewer';

/**
 * Iter 235.d (Carlos): Componente reutilizable para visualizar documentos
 * relacionados a una factura. Renderiza 3 mini-botones:
 *
 * - DTE: abre modal con FacturaViewer (datos enriquecidos del DTE)
 * - OC: abre modal interno con OCViewer (igual que en AR — Iter 236.m)
 * - Recep: descarga PDF de la recepción y lo abre en pestaña
 *
 * Props:
 *   folio: string (folio del DTE)
 *   rutEmisor: string
 *   numeroOc: string | null
 *   numeroRecepcion: string | null
 *   size: 'sm' | 'md' (default 'sm')
 */
export function DteDocsActions({
  folio,
  rutEmisor = '',
  numeroOc = '',
  numeroRecepcion = '',
  size = 'sm',
}) {
  const [dteModal, setDteModal] = useState({ open: false, loading: false, dte: null, ops: [] });
  // Iter 236.m (Carlos): modal interno para OC (en vez de abrir nueva pestaña)
  const [ocModal, setOcModal] = useState({ open: false, loading: false, ocData: null });
  const [recepLoading, setRecepLoading] = useState(false);

  const iconClass = size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  const btnClass = size === 'md' ? 'p-1.5' : 'p-1';

  const handleViewDTE = async () => {
    if (!folio) return;
    setDteModal({ open: true, loading: true, dte: null, ops: [] });
    try {
      const params = rutEmisor ? `?rut_emisor=${encodeURIComponent(rutEmisor)}` : '';
      const res = await api.get(`/contabilidad/acepta-rechazo-dte/detalle/${folio}${params}`);
      setDteModal({
        open: true,
        loading: false,
        dte: res.data.dte || null,
        ops: res.data.operaciones || [],
      });
    } catch (e) {
      // Iter 236.j (Carlos): NO cerramos silenciosamente. Mostramos el modal
      // con el mensaje "No se encontró el DTE" para que el usuario sepa qué pasó.
      setDteModal({ open: true, loading: false, dte: null, ops: [] });
    }
  };

  const handleViewRecep = async () => {
    if (!numeroRecepcion) return;
    setRecepLoading(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${api.defaults.baseURL}/bodega/recepcion/${numeroRecepcion}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        window.alert(`No se pudo abrir la recepción ${numeroRecepcion}`);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } finally {
      setRecepLoading(false);
    }
  };

  const handleViewOC = async () => {
    if (!numeroOc) return;
    // Iter 236.m (Carlos): modal interno con OCViewer — mismo patrón que en AR.
    // Antes: window.open a /compras/seguimiento → sacaba al usuario de la nómina.
    setOcModal({ open: true, loading: true, ocData: null });
    try {
      const res = await api.get(`/compras/orden-compra/${encodeURIComponent(numeroOc)}/detalle-completo`);
      setOcModal({ open: true, loading: false, ocData: res.data });
    } catch (e) {
      setOcModal({ open: true, loading: false, ocData: null });
    }
  };

  return (
    <>
      <div className="inline-flex items-center gap-0.5" data-testid={`docs-actions-${folio}`}>
        <button
          onClick={handleViewDTE}
          className={`${btnClass} rounded hover:bg-blue-50 text-blue-600 hover:text-blue-800 transition-colors`}
          title="Ver Factura DTE (con dirección, ítems, referencias)"
          data-testid={`doc-dte-${folio}`}
        >
          <FileText className={iconClass} />
        </button>
        {numeroOc ? (
          <button
            onClick={handleViewOC}
            className={`${btnClass} rounded hover:bg-green-50 text-green-600 hover:text-green-800 transition-colors`}
            title={`Ver OC ${numeroOc}`}
            data-testid={`doc-oc-${folio}`}
          >
            <ClipboardList className={iconClass} />
          </button>
        ) : (
          <span className={`${btnClass} text-gray-200`} title="Sin OC vinculada">
            <ClipboardList className={iconClass} />
          </span>
        )}
        {numeroRecepcion ? (
          <button
            onClick={handleViewRecep}
            disabled={recepLoading}
            className={`${btnClass} rounded hover:bg-purple-50 text-purple-600 hover:text-purple-800 transition-colors`}
            title={`Ver Recepción ${numeroRecepcion}`}
            data-testid={`doc-recep-${folio}`}
          >
            {recepLoading ? <Loader2 className={`${iconClass} animate-spin`} /> : <ScanLine className={iconClass} />}
          </button>
        ) : (
          <span className={`${btnClass} text-gray-200`} title="Sin recepción asociada">
            <ScanLine className={iconClass} />
          </span>
        )}
      </div>

      {/* DTE Modal */}
      {dteModal.open && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setDteModal({ open: false, loading: false, dte: null, ops: [] })}
          data-testid={`dte-modal-${folio}`}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b px-4 py-2 flex items-center justify-between z-10">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                Factura DTE — Folio {folio}
                {rutEmisor && <span className="text-xs text-gray-500 font-mono">{rutEmisor}</span>}
              </h3>
              <button
                onClick={() => setDteModal({ open: false, loading: false, dte: null, ops: [] })}
                className="p-1 rounded hover:bg-gray-100"
                data-testid={`close-dte-modal-${folio}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              {dteModal.loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-sm text-gray-500">Cargando DTE…</span>
                </div>
              ) : dteModal.dte ? (
                <FacturaViewer dte={dteModal.dte} operaciones={dteModal.ops} />
              ) : (
                <div className="text-center py-12 text-amber-700">
                  <p>No se encontró el DTE para folio {folio}.</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Puede no estar sincronizado aún o haber sido descartado.
                  </p>
                  {rutEmisor && (
                    <a
                      href={`/contabilidad/registro-dte`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs mt-3"
                    >
                      Buscar en Registro DTE <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* OC Modal (Iter 236.m — modal interno, no nueva pestaña) */}
      {ocModal.open && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setOcModal({ open: false, loading: false, ocData: null })}
          data-testid={`oc-modal-${folio}`}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b px-4 py-2 flex items-center justify-between z-10">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Package className="w-4 h-4 text-[#003366]" />
                Orden de Compra {numeroOc}
              </h3>
              <button
                onClick={() => setOcModal({ open: false, loading: false, ocData: null })}
                className="p-1 rounded hover:bg-gray-100"
                data-testid={`close-oc-modal-${folio}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              {ocModal.loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#003366]" />
                  <span className="ml-2 text-sm text-gray-500">Cargando OC…</span>
                </div>
              ) : ocModal.ocData ? (
                <OCViewer
                  oc={ocModal.ocData.oc}
                  detalle={ocModal.ocData.detalle || []}
                  proveedor={ocModal.ocData.proveedor || {}}
                  operaciones={ocModal.ocData.operaciones || []}
                  formaPago={ocModal.ocData.forma_pago || ''}
                  esTarjeta={ocModal.ocData.es_tarjeta || false}
                  empresa={ocModal.ocData.empresa || {}}
                />
              ) : (
                <div className="text-center py-12 text-amber-700">
                  <p>No se pudo cargar la OC {numeroOc}.</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Verifica que el número exista en el sistema.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DteDocsActions;
